import {
	Component,
	EventListener,
	findComponents,
	getComponentByDomNode,
	startComponents,
	stopComponents,
	hasMountedGondelComponent,
} from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { TextInput } from '@swisslife/patterns-base/src/atoms/text-input/js/text-input';
import { Form } from '@swisslife/patterns-base/src/organisms/form/js/form';
import { IErrorTemplate } from '@swisslife/patterns-base/src/organisms/form/js/initParsley';
import { FormElement } from '@swisslife/patterns-base/src/molecules/form-element/js/form-element';

// add your states here, e.g. Hidden: 'state-m-appointment-scheduler--hidden',
enum States {
	Deletable = 'state-m-appointment-scheduler--deletable',
	Error = 'state-m-appointment-scheduler--error',
}

// add your selector here, e.g. Element = '.js-m-appointment-scheduler',
enum Selectors {
	AddEntry = '.js-m-appointment-scheduler__add-entry-button',
	DeleteEntryButton = '.js-m-appointment-scheduler__delete-entry-button',
	Wrapper = '.js-m-appointment-scheduler__wrapper',
	Entry = '.js-m-appointment-scheduler__entry',
	Form = '.js-o-form',
	FormElement = '.js-m-form-element--date-picker',
	TextInput = '.js-a-text-input',
}

type OpenCallbackFunction = () => any | never;
type ChangeCallbackFunction = (newDate: Date, newDateString: string) => any | never;

interface IEntryComponentsAndCallbacks {
	date: FormElement | null;
	timeFrom: FormElement | null;
	timeUntil: FormElement | null;
	timeFromOpenCallback: OpenCallbackFunction;
	timeUntilOpenCallback: OpenCallbackFunction;
	timeFromChangeCallback: ChangeCallbackFunction;
	timeUntilChangeCallback: ChangeCallbackFunction;
}

// The @Component decorator will connect the class with `data-g-name="AppointmentScheduler"` elements.
@Component('AppointmentScheduler')
export class AppointmentScheduler extends GondelJqueryComponent {
	private $template: JQuery<HTMLElement>;
	private $wrapper: JQuery<HTMLElement>;
	private $form: JQuery<HTMLElement>;
	private $serverSideError: JQuery<HTMLElement> | null = null;
	private entryCount: number = 1;
	private rowCount: number = 1;
	private form: Form;
	private isRequired: boolean = false;
	private entriesComponentsAndCallbacks: IEntryComponentsAndCallbacks[] = [];
	private baseGroupName: string;

	public start() {
		this.$wrapper = this.$ctx.find(Selectors.Wrapper);
		this.$form = this.$ctx.closest(Selectors.Form);
		this.baseGroupName = this.$ctx.find(Selectors.Entry).data('group-name');

		if (this.$form.length > 0 && hasMountedGondelComponent(this.$form)) {
			this.form = getComponentByDomNode<Form>(this.$form);
			this.form.registerFieldServerSideErrorCallback(this.baseGroupName, this.handleServerSideErrorCallback);
			if (this.baseGroupName) {
				this.isRequired = true;
				this.form.registerValidationInitCallback(this.handleFormValidationInit);
			}
		}
	}

	public sync() {
		// clone needs to be in sync() so that bin-icon loads correctly in IE11 timing issue with svg4everybody
		this.$template = this.$ctx.find(Selectors.Entry).clone();
		this.setDynamicFieldValues(this.$ctx.find(Selectors.Entry));
		this.addChangeListeners(this.$ctx.find(Selectors.Entry));
	}

	@EventListener('click', Selectors.AddEntry)
	public _handleAddEntryClick(ev: MouseEvent) {
		ev.preventDefault();

		const $newEntry = this.$template.clone();

		stopComponents($newEntry);

		$newEntry.hide();
		this.$wrapper.append($newEntry);
		$newEntry.slideDown();

		startComponents($newEntry);

		this.setDynamicFieldValues($newEntry);

		this.rowCount++;
		this.checkDeleteButtonState();
		this.addChangeListeners($newEntry);

		if (this.isRequired) {
			this.form.updateValidationAfterContentChange();
		}
	}

	@EventListener('click', Selectors.DeleteEntryButton)
	public _handleDeleteEntryClick(ev: JQuery.ClickEvent<HTMLDivElement, any, HTMLAnchorElement | HTMLButtonElement>) {
		ev.preventDefault();

		const $entry = $(ev.target!).closest(Selectors.Entry);

		this.removeChangeListeners($entry);

		stopComponents($entry);

		$entry.slideUp(() => {
			$entry.remove();
		});

		this.rowCount--;
		this.checkDeleteButtonState();

		if (this.isRequired) {
			this.form.updateValidationAfterContentChange();
		}
	}

	private setDynamicFieldValues($entry: JQuery<HTMLElement>) {
		if (this.isRequired) {
			$entry.attr('data-group-name', `${this.baseGroupName}-${this.entryCount}`);
		}

		const inputFields = findComponents<TextInput>($entry.get(0), 'TextInput');

		inputFields.forEach((textInput: TextInput) => {
			textInput.setAttribute('id', `${textInput.getAttribute('id')}${this.entryCount}`);
			textInput.setAttribute('name', `${textInput.getAttribute('name')}${this.entryCount}`);

			if (this.isRequired) {
				textInput.setAttribute('data-parsley-group', `${this.baseGroupName}-${this.entryCount}`);
			}
		});

		$entry.data('index', this.entryCount - 1);

		this.entryCount++;
	}

	private addChangeListeners($entry: JQuery<HTMLElement>) {
		const $formElements = $entry.find(Selectors.FormElement);

		if ($formElements.length > 0) {
			const formElements = findComponents<FormElement>($entry, 'FormElement');

			const date = formElements[0];
			const timeFrom = formElements[1];
			const timeUntil = formElements[2];

			// we add a open callback to update min / max option based on the value of the other field
			const timeFromOpenCallback = this.getDatePickerOpenCallbackFunction(timeFrom, timeUntil, 'maxTime');
			const timeUntilOpenCallback = this.getDatePickerOpenCallbackFunction(timeUntil, timeFrom, 'minTime');

			// we add a change callback to update min / max option of the other field based on the changed value
			const timeFromChangeCallback = this.getDatePickerChangeCallbackFunction(timeUntil, 'minTime');
			const timeUntilChangeCallback = this.getDatePickerChangeCallbackFunction(timeFrom, 'maxTime');

			timeFrom.addDatePickerOpenCallback(timeFromOpenCallback);
			timeUntil.addDatePickerOpenCallback(timeUntilOpenCallback);

			timeFrom.addDatePickerChangeCallback(timeFromChangeCallback);
			timeUntil.addDatePickerChangeCallback(timeUntilChangeCallback);

			this.entriesComponentsAndCallbacks.push({
				date,
				timeFrom,
				timeUntil,
				timeFromChangeCallback,
				timeUntilChangeCallback,
				timeFromOpenCallback,
				timeUntilOpenCallback,
			});
		}
	}

	private removeChangeListeners($entry: JQuery) {
		const index = $entry.data('index');

		if (index >= 0) {
			const timeFormElementObject = this.entriesComponentsAndCallbacks[index];

			timeFormElementObject.timeFrom!.removeDatePickerOpenCallback(timeFormElementObject.timeFromOpenCallback);
			timeFormElementObject.timeUntil!.removeDatePickerOpenCallback(timeFormElementObject.timeUntilOpenCallback);

			timeFormElementObject.timeFrom!.removeDatePickerChangeCallback(
				timeFormElementObject.timeFromChangeCallback
			);
			timeFormElementObject.timeUntil!.removeDatePickerChangeCallback(
				timeFormElementObject.timeUntilChangeCallback
			);

			// we cannot remove the object from the array, as we continue with the index for new rows
			this.entriesComponentsAndCallbacks[index] = {
				date: null,
				timeFrom: null,
				timeUntil: null,
				timeFromChangeCallback: () => {}, // tslint:disable-line
				timeUntilChangeCallback: () => {}, // tslint:disable-line
				timeFromOpenCallback: () => {}, // tslint:disable-line
				timeUntilOpenCallback: () => {}, // tslint:disable-line
			};
		}
	}

	private getDatePickerChangeCallbackFunction(formElement: FormElement, optionName: string) {
		return (newDate: Date, newDateString: string) => {
			formElement.setOption(optionName, newDateString);
		};
	}

	private getDatePickerOpenCallbackFunction(
		callbackFormElement: FormElement,
		otherFormElement: FormElement,
		optionName: string
	) {
		return () => {
			const dateString = otherFormElement.getDate();

			if (dateString !== '') {
				callbackFormElement.setOption(optionName, dateString);
			}
		};
	}

	private checkDeleteButtonState() {
		if (this.rowCount === 1) {
			this.$ctx.removeClass(States.Deletable);
			this.$ctx.find(Selectors.DeleteEntryButton).attr('disabled', 'disabled');
			this.$ctx.find(Selectors.DeleteEntryButton).attr('aria-disabled', 'true');
		} else {
			this.$ctx.addClass(States.Deletable);
			this.$ctx.find(Selectors.DeleteEntryButton).removeAttr('disabled');
			this.$ctx.find(Selectors.DeleteEntryButton).removeAttr('aria-disabled');
		}
	}

	private handleFormValidationInit = () => {
		this.form.addGroupValidation(Selectors.Entry, States.Error, this.handleValidateCallback);
	};

	private handleServerSideErrorCallback = (
		errorMessageArray: string[],
		errorTemplate: IErrorTemplate,
		isFirstError: boolean
	) => {
		const $errorWrapper = $(errorTemplate.errorsWrapper);
		const $errorTemplate = $(errorTemplate.errorTemplate);

		errorMessageArray.forEach((errorMessage) => {
			const $error = $errorTemplate.clone();
			$error.text(errorMessage);
			$errorWrapper.append($error);
		});

		// remove possible remaining errors from previous server-side validation when not having client-side validation
		this.handleValidateCallback();

		// mark partially filled rows with error class
		this.setFieldsErrorState(true);

		this.$ctx.append($errorWrapper);
		this.$serverSideError = $errorWrapper;

		if (this.form && isFirstError) {
			this.form.handleFormValidation(this.$ctx.find(Selectors.TextInput).first());
		}
	};

	private setFieldsErrorState(newState: boolean) {
		this.entriesComponentsAndCallbacks.forEach((entryObject: IEntryComponentsAndCallbacks) => {
			// rows with form elements being null where previously deleted
			if (entryObject.date === null && entryObject.timeFrom === null && entryObject.timeUntil === null) {
				return;
			}

			const formElements = [entryObject.date, entryObject.timeFrom, entryObject.timeUntil];

			const allFieldsAreEmpty = formElements.every((formElement: FormElement) => {
				return formElement.textInput!.value === '';
			});

			const allFieldsAreFilled = formElements.every((formElement: FormElement) => {
				return formElement.textInput!.value !== '';
			});

			if (allFieldsAreEmpty || allFieldsAreFilled) {
				return;
			}

			this.setFieldErrorState(entryObject.date, newState);
			this.setFieldErrorState(entryObject.timeFrom, newState);
			this.setFieldErrorState(entryObject.timeUntil, newState);
		});
	}

	private setFieldErrorState(formElement: FormElement | null, newState: boolean) {
		if (formElement === null) {
			return;
		}

		if (newState && formElement.textInput !== null && formElement.textInput.value === '') {
			formElement.setState('Error');
		} else {
			formElement.removeState('Error');
		}
	}

	private handleValidateCallback = () => {
		if (this.$serverSideError !== null) {
			this.$serverSideError.remove();
			this.$serverSideError = null;

			// remove "static" error classes again
			this.setFieldsErrorState(false);
		}
	};
}
